import React, { useEffect, useRef, useState } from 'react';
import {
  FaChevronDown,
  FaMicrophone,
  FaMicrophoneSlash,
  FaSlidersH,
  FaSyncAlt,
  FaVolumeUp,
} from 'react-icons/fa';
import { useMicrophoneDevices } from './useMicrophoneDevices';
import { useOverlay } from '../../contexts/FocusContext';
import {
  INITIAL_VOICE_STATE,
  parseVoiceBridgeState,
  sendVoiceCommand,
  type VoiceBridgeState,
} from './localVoiceBridge';

interface VoiceControlProps {
  unityReady: boolean;
}

const statusLabel: Record<VoiceBridgeState['status'], string> = {
  loading: 'Starting voice…',
  disabled: 'Voice unavailable',
  disconnected: 'Voice disconnected',
  connecting: 'Connecting voice…',
  connected: 'Voice connected',
};

const VoiceControl: React.FC<VoiceControlProps> = ({ unityReady }) => {
  const [voice, setVoice] = useState<VoiceBridgeState>(INITIAL_VOICE_STATE);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const microphone = useMicrophoneDevices(settingsOpen, voice);
  const requestedConnectRef = useRef(false);

  useOverlay('intersect-voice-control', settingsOpen);

  useEffect(() => {
    const handleState = (event: Event) => {
      const next = parseVoiceBridgeState((event as CustomEvent).detail);
      if (!next) return;
      setVoice(next);

      if (next.connected) requestedConnectRef.current = false;
      if (
        next.ready &&
        next.enabled &&
        !next.connected &&
        next.status === 'disconnected' &&
        !requestedConnectRef.current
      ) {
        requestedConnectRef.current = sendVoiceCommand('connect');
      }
    };

    window.addEventListener('VoiceStateChanged', handleState);
    return () => window.removeEventListener('VoiceStateChanged', handleState);
  }, []);

  useEffect(() => {
    if (!unityReady) return;
    sendVoiceCommand('getState');
    const retry = window.setTimeout(() => sendVoiceCommand('getState'), 800);
    return () => window.clearTimeout(retry);
  }, [unityReady]);

  const handleMicClick = () => {
    if (!voice.ready || !voice.enabled) return;
    if (!voice.connected) {
      requestedConnectRef.current = sendVoiceCommand('connect');
      setVoice((current) => ({ ...current, status: 'connecting' }));
      return;
    }

    const muted = !voice.muted;
    setVoice((current) => ({ ...current, muted }));
    sendVoiceCommand('setMuted', { muted });
  };

  const stopUnityPointer = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <div
      className="unity-voice-control unity-custom-cursor"
      onClick={stopUnityPointer}
      onPointerDown={stopUnityPointer}
      onContextMenu={stopUnityPointer}
    >
      <div className="unity-voice-control__bar">
        <button
          type="button"
          className={`unity-voice-control__mic ${voice.muted ? 'is-muted' : 'is-live'}`}
          onClick={handleMicClick}
          disabled={!voice.ready || !voice.enabled || voice.status === 'connecting'}
          aria-label={voice.connected ? (voice.muted ? 'Unmute microphone' : 'Mute microphone') : 'Connect voice'}
          aria-pressed={voice.connected ? !voice.muted : undefined}
        >
          {voice.muted || !voice.connected ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>

        <button
          type="button"
          className="unity-voice-control__summary"
          onClick={() => setSettingsOpen((open) => !open)}
          aria-expanded={settingsOpen}
          aria-label="Open microphone and voice settings"
        >
          <span className={`unity-voice-control__dot is-${voice.status}`} />
          <span>
            <strong>Voice</strong>
            <small>{voice.connected ? (voice.muted ? 'Muted' : 'Voice on') : statusLabel[voice.status]}</small>
          </span>
          <FaChevronDown className={settingsOpen ? 'is-open' : ''} />
        </button>
      </div>

      {settingsOpen && (
        <div className="unity-voice-control__panel" role="dialog" aria-label="Voice settings">
          <div className="unity-voice-control__heading">
            <span><FaSlidersH /> Voice settings</span>
            {!voice.connected && voice.enabled && (
              <button type="button" onClick={handleMicClick} disabled={voice.status === 'connecting'}>
                <FaSyncAlt /> Retry
              </button>
            )}
          </div>

          <label>
            <span><FaMicrophone /> Microphone input</span>
            <select
              aria-label="Microphone input"
              value={microphone.selected}
              disabled={microphone.busy || !microphone.canSwitch || microphone.devices.length === 0}
              onChange={(event) => void microphone.select(event.target.value)}
            >
              <option value="" disabled>{microphone.busy ? 'Loading microphones…' : 'Current / system default microphone'}</option>
              {microphone.devices.map((device) => <option key={device.id} value={device.id}>{device.label}</option>)}
            </select>
          </label>
          <div className="unity-voice-control__device-actions">
            <button type="button" disabled={microphone.busy || !microphone.supported} onClick={() => void microphone.refresh()}>
              <FaSyncAlt /> Refresh microphones
            </button>
            {microphone.supported && microphone.needsPermission && (
              <button type="button" disabled={microphone.busy || !voice.enabled} onClick={() => void microphone.refresh(true)}>
                Allow microphone access
              </button>
            )}
          </div>
          {microphone.error ? (
            <p className="unity-voice-control__device-help is-error" role="alert">{microphone.error}</p>
          ) : (
            <p className="unity-voice-control__device-help" role="status">
              {!voice.enabled ? 'Voice is unavailable in this world.' : !voice.connected ? 'Connect voice to choose a microphone.' :
                microphone.needsPermission ? 'Allow microphone access to show available inputs and their names.' :
                !microphone.canSwitch && microphone.devices.length > 0 ? 'Unmute to activate microphone selection.' :
                microphone.devices.length === 0 ? 'No microphone inputs found. Check your device or browser microphone settings.' :
                'Choose the microphone used for your voice in the world.'}
            </p>
          )}

          <label>
            <span><FaMicrophone /> Microphone level</span>
            <output>{Math.round(voice.inputVolume * 100)}%</output>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={voice.inputVolume}
              disabled={!voice.connected}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVoice((current) => ({ ...current, inputVolume: value }));
                sendVoiceCommand('setInputVolume', { value });
              }}
            />
          </label>

          <label>
            <span><FaVolumeUp /> Voice volume</span>
            <output>{Math.round(voice.outputVolume * 100)}%</output>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={voice.outputVolume}
              disabled={!voice.connected}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVoice((current) => ({ ...current, outputVolume: value }));
                sendVoiceCommand('setOutputVolume', { value });
              }}
            />
          </label>

          {voice.outputDevices.length > 0 && (
            <label>
              <span>Speaker</span>
              <select
                value={voice.outputDevice}
                onChange={(event) => sendVoiceCommand('setOutputDevice', { deviceName: event.target.value })}
              >
                {voice.outputDevices.map((device) => <option key={device}>{device}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceControl;
